import { t } from "i18next";
import { useState } from "react";
import { ActionValidator } from "../../../../auth/actionValidator";

import { ToastUtilities } from "@libs/react-toastify/ToastUtilities";
import { ActionButton } from "@src/components/deprecated/ActionButton/ActionButton";
import { useAPISettings } from "@src/data-hooks/general-settings/settings.service";
import { getSingularRoleTitle, getSingularTenantUnitTitle } from "@src/utils/data/roles.utils";
import { getMemberVisibilityType, isMemberVisible } from "@src/utils/settings/settings.utils";
import { arrayRemove } from "firebase/firestore";
import { ActionsPopover, ActionsPopoverAction } from "../../../../components/deprecated/ActionsPopover/ActionsPopover";
import { Label } from "../../../../components/deprecated/Label/Label";
import { Popup } from "../../../../components/deprecated/Popup/Popup";
import { PopupTemplates } from "../../../../components/deprecated/PopupTemplates/PopupTemplates";
import { UnitSelectPopup } from "../../../../components/deprecated/UnitSelectPopup/UnitSelectPopup";
import { LoadingIndicator } from "../../../../components/ui-components/LoadingIndicator/LoadingIndicator";
import { MemberData } from "../../../../to-be-removed/LocalData/Property/MemberData";
import { PropertyData } from "../../../../to-be-removed/LocalData/Property/PropertyData";
import { Role } from "../../../../to-be-removed/LocalData/User/Role";

type Props = {
  property: PropertyData;
  member: MemberData;
  dataCy: string;
};

export const PropertyMemberAction = (props: Props) => {
  const [loading, setLoading] = useState(false);
  const { settings } = useAPISettings({ propertyID: props.property.id });

  function saveUpdate(data: { [key: string]: any }) {
    setLoading(true);
    props.member.saveUpdateWithCompletionHandler(data, (error) => {
      if (error) ToastUtilities.presentError(t("main.errorOccured"));
      setLoading(false);
    });
  }

  const actions: ActionsPopoverAction[] = [];
  let roleIdentifier = props.member.hasLandlord()
    ? "landlord"
    : props.member.hasEntrepreneur()
      ? "contractor"
      : props.member.hasTenant()
        ? "tenant"
        : undefined;
  if (roleIdentifier && ActionValidator.isAllowedToPerformAction("property_edit_role_title_" + roleIdentifier, [props.property])) {
    actions.push({
      title: t("main.editTitle"),
      iconColor: "var(--primary-color)",
      iconName: "badge",
      onClick: () => {
        PopupTemplates.presentTextInput({
          title: t("main.editMemberTitle"),
          placeholder: t("main.editTitle"),
          onConfirm: (popupId, title) => {
            Popup.remove(popupId);
            saveUpdate({
              roleTitle: title,
            });
          },
          actionTitle: t("main.confirm"),
        });
      },
    });
  }
  if (
    ActionValidator.isAllowedToPerformAction(
      props.member.isAdmin ? "property_kick_landlord" : "property_kick_landlord",
      [props.property],
      props.member.user
    ) &&
    props.member.hasLandlord()
  ) {
    actions.push({
      title: t("main.removeAs") + " " + getSingularRoleTitle(Role.LANDLORD.id, settings?.data).toLowerCase(),
      iconColor: "var(--main-red-700)",
      iconName: "person_remove",
      onClick: () => {
        PopupTemplates.presentVerifyAction(
          t("main.confirmRemoveMemberAs") + " " + getSingularRoleTitle(Role.LANDLORD.id, settings?.data).toLowerCase() + "?",
          t("main.cannotBeUndone"),
          () => {
            saveUpdate({
              roles: arrayRemove(1),
            });
          },
          t("main.confirm")
        );
      },
    });
  }

  if (ActionValidator.isAllowedToPerformAction("property_kick_tenant", [props.property], props.member.user) && props.member.hasTenant()) {
    actions.push({
      title: t("main.removeAs") + " " + getSingularRoleTitle(Role.RENTER.id, settings?.data).toLowerCase(),
      iconColor: "var(--main-red-700)",
      iconName: "person_remove",
      onClick: () => {
        Popup.present((id) => (
          <UnitSelectPopup
            id={id}
            title={t("main.select") + " " + getSingularTenantUnitTitle(undefined).toLowerCase() + " " + t("main.toRemoveMemberFrom")}
            placeholder={t("main.select") + " " + getSingularTenantUnitTitle(undefined).toLowerCase()}
            property={props.property}
            disallowEmpty={true}
            defaultSelectedUnits={props.member.units}
            units={props.member.units}
            onSelect={(units) => {
              saveUpdate({
                units: arrayRemove(...units.map((unit) => unit.id)),
              });
            }}
          />
        ));
      },
    });
  }
  if (
    props.member.isAdmin &&
    ((!props.member.hasLandlord() && ActionValidator.isAllowedToPerformAction("property_remove_tenant_admin", [props.property])) ||
      ActionValidator.isAllowedToPerformAction("property_remove_admin", [props.property]))
  ) {
    actions.push({
      title: t("main.removeAsSuperUser"),
      iconColor: "var(--main-red-700)",
      iconName: "remove_moderator",
      onClick: () => {
        saveUpdate({
          admin: false,
        });
      },
    });
  } else if (
    (!props.member.isAdmin &&
      !props.member.hasLandlord() &&
      ActionValidator.isAllowedToPerformAction("property_assign_tenant_admin", [props.property])) ||
    ActionValidator.isAllowedToPerformAction("property_assign_admin", [props.property])
  ) {
    actions.push({
      title: t("main.assignAsSuperUser"),
      iconColor: "var(--primary-color)",
      iconName: "admin_panel_settings",
      onClick: () => {
        saveUpdate({
          admin: true,
        });
      },
    });
  }

  if (ActionValidator.isAllowedToPerformAction("property_administrate_operation_manager", [props.property])) {
    if (props.member.isOperationManager) {
      actions.push({
        title: t("main.removeAsOperationManager"),
        iconColor: "var(--main-red-700)",
        iconName: "admin_panel_settings",
        onClick: () => {
          saveUpdate({
            operationManager: false,
          });
        },
      });
    } else {
      actions.push({
        title: t("main.assignAsOperationManager"),
        iconColor: "var(--primary-color)",
        iconName: "engineering",
        onClick: () => {
          saveUpdate({
            operationManager: true,
          });
        },
      });
    }
  }

  if (roleIdentifier && ActionValidator.isAllowedToPerformAction("property_edit_visibility_" + roleIdentifier, [props.property])) {
    let visibilityType = getMemberVisibilityType(props.member, settings?.data);
    let title =
      visibilityType === "opt-out"
        ? props.member.visibility.hidden
          ? t("main.memberVisibility_removeHidden")
          : t("main.memberVisibility_makeHidden")
        : props.member.visibility.visible
          ? t("main.memberVisibility_removeVisible")
          : t("main.memberVisibility_makeVisible");
    actions.push({
      title: title,
      iconColor: "var(--primary-color)",
      iconName: !isMemberVisible(props.member, settings?.data) ? "visibility" : "visibility_off",
      onClick: () => {
        let newData: { [key: string]: any } = {};
        if (visibilityType === "opt-out") newData["hidden"] = !props.member.visibility.hidden;
        else if (visibilityType === "opt-in") newData["visible"] = !props.member.visibility.visible;

        saveUpdate({
          visibility: newData,
        });
      },
    });
  }

  const [actionsVisible, setActionsVisible] = useState(false);

  return loading ? (
    <LoadingIndicator />
  ) : actions.length > 0 ? (
    <ActionsPopover actions={actions} position={["bottom center", "bottom right"]} trigger={<ActionButton dataCy={props.dataCy} />} />
  ) : (
    <Label type="paragraph" text={t("main.none")} />
  );
};
